import { parse } from 'url';

import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { Cookies } from '@confluence/cookies';
import { getTaskManager, LoadingPriority } from '@confluence/loadable';
import { enableGraphqlRetries } from '@confluence/graphql/entry-points/enableGraphqlRetries.ts';
import { getBuildInfo } from '@confluence/build-info';
import { defaultEventMatchers } from '@confluence/filtered-awc/entry-points/allowedEvents.ts';
import {
	getMonitoringClient,
	enableSendErrorsToTelemetryService,
} from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { initializeAnalyticsClient } from '@confluence/analytics-web-client';
import { patchLocalStorage } from '@confluence/storage-manager/entry-points/patchLocalStorage.ts';
import { enableAnalyticsDebugger } from '@confluence/analytics-debugger/entry-points/AnalyticsDebugger.ts';
import { establishIPCListeners } from '@confluence/desktop-native-bridge';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { setFrontendBranch } from './set-frontend-branch.js';
import { setBrowserMetricsUniqueIdentifier } from './set-browser-metrics-unique-identifier.js';

const STATSIG_FAILED_LOGS_PREFIX = 'statsig.failed_logs.';
const STATSIG_CLEANUP_DELAY_MS = 5000;

/**
 * Clear all statsig.failed_logs.* keys from localStorage.
 * This is delayed to give Statsig a chance to retry sending failed events first.
 */
function clearStatsigFailedLogs() {
	setTimeout(() => {
		try {
			for (const key of Object.keys(localStorage)) {
				if (key.startsWith(STATSIG_FAILED_LOGS_PREFIX)) {
					localStorage.removeItem(key);
				}
			}
		} catch (e) {
			// Ignore errors - localStorage may be inaccessible
		}
	}, STATSIG_CLEANUP_DELAY_MS);
}

export function initialization(sessionData, { browserName } = {}) {
	const { featureFlags, cloudId, environment, shard, activationId, edition } = sessionData;

	// Note this is a temporary fix that will be removed when a fix is deployed in statsig: https://product-fabric.atlassian.net/browse/CCP-7527
	const blockStatsigFailuresLocalStorage = StatsigConfigurations.getBoolean(
		'confluence_frontend_block_statsig_local_storage',
		false,
	);
	if (blockStatsigFailuresLocalStorage) {
		clearStatsigFailedLogs();
	}
	patchLocalStorage(blockStatsigFailuresLocalStorage);
	setFrontendBranch();

	if (featureFlags['confluence.frontend.bm.unique-identifier']) {
		setBrowserMetricsUniqueIdentifier();
	}

	enableGraphqlRetries();
	enableSendErrorsToTelemetryService();

	const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();
	const product = isEmbeddedConfluence ? 'embeddedConfluence' : 'confluence';
	//Grabbing parentProduct and setting it to subproduct
	const subproduct = isEmbeddedConfluence
		? parse(location.search, true).query.parentProduct
		: undefined;

	// Monitoring context initialization
	getMonitoringClient().updateContext({
		tenantId: cloudId,
		shard,
		product,
		edition,
	});

	// Before initializing the Analytics Web Client (AWC), set the
	// `atlassian_analytics_debug` cookie to indicate that CC Scale bot users (load testing)
	// can use `window.testAnalytics` object to detect whether a page is loaded
	// (AWC saves all the events that are fired under `window.testAnalytics`)
	const analyticsLoadTesting = StatsigConfigurations.getBoolean(
		'confluence_frontend_analytics_load_testing',
		false,
	);
	let analyticsDebuggingEnabled = analyticsLoadTesting;

	if (expValEquals('cc_editor_clean_traffic', 'analyticsDebuggingEnabled', true)) {
		const ufoGroup = StatsigConfigurations.getString('cc_ufo_group', '');
		const isTestTenant = ufoGroup === 'cust_test' || ufoGroup === 'intn_test';
		const SYNTHETIC_BROWSER_NAMES = StatsigConfigurations.getStringList(
			'cc_synthetic_browsers_list',
			['HeadlessChrome', 'bingbot', 'Catchpoint', 'HubSpot Crawler'],
		);
		const isSyntheticBrowser =
			(typeof navigator !== 'undefined' && navigator.webdriver) ||
			(browserName !== undefined && SYNTHETIC_BROWSER_NAMES.includes(browserName));

		analyticsDebuggingEnabled = analyticsLoadTesting || isTestTenant || isSyntheticBrowser;
	}

	const shouldFilterEvents = Boolean(
		StatsigConfigurations.isKillSwitchOn('confluence_frontend_filter_events_config') ?? false,
	);

	if (analyticsDebuggingEnabled) {
		Cookies.set('atlassian_analytics_debug', true);
	}

	// Disable AWC synthetic tag via global override when feature gate is enabled
	const disableAWCSyntheticTag = StatsigConfigurations.getBoolean(
		'confluence_frontend_awc_disable_synthetic_tag',
		false,
	);
	if (disableAWCSyntheticTag) {
		// Explicitly disable automatic 'synthetic' tag on AWC events
		// See platform/packages/atlassian-analytics-pipeline/analytics-web-client/README.md
		// and TestSupport.SYNTHETIC_FLAG_OVERRIDE_PROPERTY
		window.__awc_synthetic_flag_override__ = false;
	}

	// GASv3 analytics client
	// ActivationId: sessionData.activationId needs to be added after https://experimentation-platform.atlassian.net/browse/MEP-2924
	initializeAnalyticsClient({
		cloudId,
		activationId,
		userId: sessionData.userIdForAnalytics,
		locale: sessionData.locale,
		environment: StatsigConfigurations.getBoolean(
			'confluence_frontend_pollinator_synthetic_tenants',
			false,
		)
			? 'DEVELOPMENT'
			: environment,
		product,
		subproduct,
		eventFilterOptions: {
			shouldFilterEvents,
			eventMatchers: defaultEventMatchers,
		},
		callback: analyticsDebuggingEnabled ? enableAnalyticsDebugger : undefined,
	});

	// Please put stuff that can be delayed here
	getTaskManager().push({
		id: 'BackgroundInitialization',
		priority: LoadingPriority.BACKGROUND,
		task: () => {
			// Global function available for support engineer to get all environmental data for troubleshooting.
			window.__CONFLUENCE_SUPPORT = () => {
				const supportData = {
					version: getBuildInfo().FRONTEND_VERSION,
					tinymce: window.tinymce
						? `${window.tinymce.majorVersion}.${window.tinymce.minorVersion}`
						: undefined,
					activeEditor: window.tinymce && window.tinymce.activeEditor ? true : undefined,
				};
				return JSON.stringify(supportData);
			};

			// Register service worker
			import(
				/* webpackChunkName: "loadable-service-workerclient" */ './service-worker/client.js'
			).then(({ unregister: unregisterServiceWorker }) => {
				unregisterServiceWorker();
			});

			// Message queue for sending GASv3 event from legacy pages
			import(
				/* webpackChunkName: "loadable-legacy-message-queue" */ './legacy-message-queue/index.js'
			).then(({ initializeLegacyMessageQueue }) => {
				initializeLegacyMessageQueue();
			});

			// https://developer.mozilla.org/en-US/docs/Glossary/Domain_sharding
			// We roughly has 8% of users still on http/1.1 https://redash.data.internal.atlassian.com/queries/126773/source#242723
			// Http/1.1 is 2x slower than http/2 https://redash.data.internal.atlassian.com/queries/126772/source#242719
			import(/* webpackChunkName: "loadable-domain-sharding" */ './domain-sharding.js').then(
				({ initializeFlag }) => initializeFlag(),
			);

			// Establish IPC listeners for Confluence Desktop App
			const shouldInitializeDesktop = StatsigConfigurations.getBoolean(
				'confluence_should_initialize_desktop',
				false,
			);
			if (shouldInitializeDesktop) {
				establishIPCListeners();
			}
		},
	});
}
